import React, { useState } from "react";
import { db } from "../../../firebaseClient";
import { useAuth } from "../../context/AuthContext";
import { isSuperUser } from "../../config/roles";

export interface RelatedNos {
  contractReviewNo?: string;
  sealNo?: string;
  brandAssetNo?: string;
  caseNo?: string;
  disputeNo?: string;
  ipNo?: string;
  contractNo?: string;
  legacyContractNo?: string;
  [key: string]: string | string[] | undefined;
}

interface Props {
  relatedNos?: RelatedNos;
  linkKey?: string;
  onChange: (data: { relatedNos: RelatedNos; linkKey?: string }) => void;
}

export function SystemLinkagePanel({ relatedNos = {}, linkKey, onChange }: Props) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  // Fix: Cast key to string to safely use .replace
  const handleAdd = (key: keyof RelatedNos) => {
    const no = prompt(`請輸入 ${String(key).replace("No", "")} 編號:`);
    if (!no) return;
    const next = { ...relatedNos, [key]: no.trim() };
    onChange({ relatedNos: next, linkKey });
  };

  // Fix: Cast key to any to avoid symbol/number key indexing errors
  const handleClear = (key: keyof RelatedNos) => {
    const next = { ...relatedNos };
    delete next[key as any];
    onChange({ relatedNos: next, linkKey });
  };

  const handleAutoLink = async () => {
    // Collect any existing IDs
    const ids = Object.values(relatedNos).filter(v => typeof v === 'string' && v.length > 0) as string[];
    if (ids.length === 0) return alert("請先填寫任一編號作為串接依據");

    setLoading(true);
    try {
      let foundData: RelatedNos | null = null;
      let foundLinkKey: string | undefined = linkKey;

      const collections = [
        { name: "legal_contracts", idField: "contractNo" },
        { name: "approval_requests", idField: "docCode" },
        { name: "brand_assets", idField: "assetNo" },
        { name: "legal_matters", idField: "matterNo" },
        { name: "ip_assets", idField: "ipNo" }
      ];

      for (const col of collections) {
        if (foundData) break;
        for (const id of ids) {
          const q = db.collection(col.name).where(col.idField, "==", id);
          const snap = await q.limit(1).get();
          if (!snap.empty) {
            const data = snap.docs[0].data();
            // Security check
            const isSuper = isSuperUser(user?.email);
            const userDept = user?.email?.split('@')[0].toUpperCase();
            const docDept = data.department || data.dept || data.ourDept;
            
            if (isSuper || (docDept && docDept.includes(userDept))) {
              foundData = data.relatedNos || {};
              foundLinkKey = data.linkKey || foundLinkKey;
              break;
            }
          }
        }
      }

      if (foundData) {
        const next = { ...relatedNos, ...foundData };
        onChange({ relatedNos: next, linkKey: foundLinkKey });
        alert("串接完成");
      } else {
        alert("查無可串接的關聯資料");
      }
    } catch (e) {
      console.error(e);
      alert("自動串接失敗");
    } finally {
      setLoading(false);
    }
  };

  const rows = [
    { key: "contractReviewNo", label: "合約審理 (CR-)" },
    { key: "sealNo", label: "用印申請 (SEAL-)" },
    { key: "contractNo", label: "現行合約 (CT-)" },
    { key: "legacyContractNo", label: "舊合約 (CT-OLD-)" },
    { key: "brandAssetNo", label: "品牌資產 (BA-)" },
    { key: "caseNo", label: "相關案件 (CASE-)" },
    { key: "disputeNo", label: "相關爭議 (DIS-)" },
    { key: "ipNo", label: "相關智財 (IP-)" },
  ];

  return (
    <div className="section" style={{ background: "rgba(99, 102, 241, 0.05)", border: "1px solid rgba(99, 102, 241, 0.2)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h4 className="title" style={{ fontSize: 14, margin: 0 }}>🔗 系統關聯編號</h4>
        <button className="smallbtn" style={{ fontSize: 11 }} onClick={handleAutoLink} disabled={loading}>
          {loading ? "串接中..." : "⚡ 自動串接"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
        {rows.map(r => (
          <div key={r.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, borderBottom: "1px solid #222", paddingBottom: 4 }}>
            <span style={{ color: "var(--muted)" }}>{r.label}</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {relatedNos[r.key] ? (
                <span className="badge" style={{ background: "#333", border: "none", color: "#fff" }}>
                  {relatedNos[r.key]}
                  <span style={{ marginLeft: 6, cursor: "pointer", opacity: 0.6 }} onClick={() => handleClear(r.key as any)}>×</span>
                </span>
              ) : (
                <button className="smallbtn" style={{ fontSize: 10, padding: "2px 6px" }} onClick={() => handleAdd(r.key as any)}>+增加</button>
              )}
            </div>
          </div>
        ))}
      </div>
      {linkKey && <div style={{ fontSize: 11, color: "#6366f1", marginTop: 8 }}>Group Key: {linkKey}</div>}
    </div>
  );
}